// =============================================================================
// AI in the City — Main Entry Point
// =============================================================================

import { initGallery } from "./gallery";
import { initIcons } from "./icons";
import { initNeuralGrid } from "./neural-grid";
import { initStrutMultiline } from "./strut-multiline";

/**
 * Initialize scroll reveal animations for elements with data-reveal attribute
 */
function initScrollReveal(): void {
    const revealElements = document.querySelectorAll(
        "[data-reveal], [data-reveal-stagger]",
    );

    if (!revealElements.length) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("revealed");
                    observer.unobserve(entry.target);
                }
            });
        },
        {
            threshold: 0.1,
            rootMargin: "0px 0px -80px 0px",
        },
    );

    revealElements.forEach((el) => observer.observe(el));
}

/**
 * Initialize legacy scroll animations for backwards compatibility
 */
function initScrollAnimations(): void {
    const animateElements = document.querySelectorAll("[data-animate]");

    if (!animateElements.length) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animated");
                    observer.unobserve(entry.target);
                }
            });
        },
        {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px",
        },
    );

    animateElements.forEach((el) => observer.observe(el));
}

/**
 * Initialize mobile menu toggle
 */
function initMobileMenu(): void {
    const menuToggle = document.querySelector(".site-header__toggle");
    const nav = document.querySelector(".site-header__right");

    if (!menuToggle || !nav) return;

    menuToggle.addEventListener("click", () => {
        const isExpanded = menuToggle.getAttribute("aria-expanded") === "true";
        menuToggle.setAttribute("aria-expanded", String(!isExpanded));
        nav.classList.toggle("site-header__right--open");
    });
}

/**
 * Initialize smooth scroll for anchor links
 */
function initSmoothScroll(): void {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener("click", (e) => {
            const href = anchor.getAttribute("href");
            if (!href || href === "#") return;

            const target = document.querySelector(href);
            if (!target) return;

            e.preventDefault();

            const headerHeight =
                document.querySelector<HTMLElement>(".header")?.offsetHeight ||
                72;
            const targetPosition =
                target.getBoundingClientRect().top +
                window.scrollY -
                headerHeight;

            window.scrollTo({
                top: targetPosition,
                behavior: "smooth",
            });

            // Close mobile menu if open
            const nav = document.querySelector(".site-header__right");
            const menuToggle = document.querySelector(".site-header__toggle");
            if (nav?.classList.contains("site-header__right--open")) {
                nav.classList.remove("site-header__right--open");
                menuToggle?.setAttribute("aria-expanded", "false");
            }
        });
    });
}

/**
 * Initialize header scroll behavior
 */
function initHeaderScroll(): void {
    const header = document.querySelector(".header");
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener("scroll", () => {
        const currentScroll = window.scrollY;

        if (currentScroll > 100) {
            header.classList.add("header--scrolled");
        } else {
            header.classList.remove("header--scrolled");
        }

        lastScroll = currentScroll;
    });
}

/**
 * Preserve scroll position / hash when switching languages.
 *
 * On click of a `[data-lang-switch]` link we encode the current scroll
 * offset (or hash) as a query/hash on the target URL. On load we read it
 * back and restore the position before paint so there is no visible jump.
 */
const LANG_SWITCH_KEY = "ai-city:lang-scroll";

function initLangSwitchScroll(): void {
    document
        .querySelectorAll<HTMLAnchorElement>("a[data-lang-switch]")
        .forEach((link) => {
            link.addEventListener("click", () => {
                try {
                    sessionStorage.setItem(
                        LANG_SWITCH_KEY,
                        String(window.scrollY),
                    );
                } catch {
                    // sessionStorage may be unavailable (private mode); ignore.
                }
                if (window.location.hash) {
                    // If we're on an in-page anchor, mirror it onto the
                    // target URL so the new locale lands on the same section.
                    const url = new URL(link.href, window.location.origin);
                    if (!url.hash) {
                        url.hash = window.location.hash;
                        link.href = url.toString();
                    }
                }
            });
        });

    // Restore on load
    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(LANG_SWITCH_KEY);
        if (stored !== null) {
            sessionStorage.removeItem(LANG_SWITCH_KEY);
        }
    } catch {
        // ignore
    }

    if (stored === null || window.location.hash) {
        return;
    }

    const targetY = parseInt(stored, 10);
    if (Number.isNaN(targetY)) {
        return;
    }

    // Clamp the restored offset to the actual scrollable range of the new
    // page so we never end up past the bottom (which feels like "stuck in
    // the middle and can't scroll up" when later content loads).
    const clampAndScroll = (): void => {
        const max = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
        );
        const y = Math.min(Math.max(0, targetY), max);
        window.scrollTo(0, y);
    };

    let prevRestoration: ScrollRestoration | undefined;
    if ("scrollRestoration" in history) {
        prevRestoration = history.scrollRestoration;
        history.scrollRestoration = "manual";
    }

    // Apply immediately, again after `load` (images/fonts may have grown
    // the document), and once more on the next frame after load to catch
    // late layout shifts. Then restore the browser default so back/forward
    // navigation still works as expected.
    clampAndScroll();
    window.addEventListener(
        "load",
        () => {
            clampAndScroll();
            requestAnimationFrame(() => {
                clampAndScroll();
                if (
                    "scrollRestoration" in history &&
                    prevRestoration !== undefined
                ) {
                    history.scrollRestoration = prevRestoration;
                }
            });
        },
        { once: true },
    );
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
    initIcons();
    initNeuralGrid();
    initScrollReveal();
    initScrollAnimations();
    initMobileMenu();
    initSmoothScroll();
    initHeaderScroll();
    initStrutMultiline();
    initLangSwitchScroll();
    initGallery();

    console.log("AI in the City loaded");
});
